/**
 * #1384 お知らせの頻度制御。
 *
 * 旅行者ファーストで一番効くのは**出しすぎないこと**。ただし普通のアプリと事情が違う。
 *
 * **訪日客の滞在は数日**なので、「一度閉じたら7日間出さない」のような一般的な設定に
 * すると**実質二度と出ない**。逆に毎回出すとうるさい。そこで:
 *
 *   - 閉じられたら **その端末では二度と出さない**（＝興味なしと解釈。滞在が数日なので
 *     「滞在中は出さない」と実質同じで、期限管理を持たない方が単純）
 *   - 同じお知らせは **1日1回まで**（閉じずに画面遷移した場合の再表示を抑える）
 *
 * 状態は localStorage に持つ。サーバに置かないのは「滞在中」という単位を
 * サーバ側で持ちにくいのと、ゲスト（未ログイン）にも出すため。
 */

const KEY = 'yokoso.announcements.v1'

type State = {
  /** 閉じられた ID。二度と出さない。 */
  dismissed: string[]
  /** ID → 最後に表示した日 (YYYY-MM-DD)。1日1回の判定に使う。 */
  shownOn: Record<string, string>
}

const EMPTY: State = { dismissed: [], shownOn: {} }

export function todayKey(now: Date = new Date()): string {
  // 端末のローカル日付で判定する。旅行者は移動するので、UTC 基準にすると
  // 「日付が変わったのに出ない / 変わっていないのに出る」がずれて見える。
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function loadState(): State {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { ...EMPTY }
    const parsed = JSON.parse(raw) as Partial<State>
    return {
      dismissed: Array.isArray(parsed.dismissed) ? parsed.dismissed : [],
      shownOn: parsed.shownOn && typeof parsed.shownOn === 'object' ? parsed.shownOn : {},
    }
  } catch {
    // 壊れた状態で読めないより、出す方に倒す（案内が出ないより出る方が害が小さい）
    return { ...EMPTY }
  }
}

function save(state: State) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state))
  } catch {
    // ストレージが使えなくても案内自体は出す（毎回出るだけ）
  }
}

/** サーバに送る除外 ID。閉じたもの + 今日すでに出したもの。 */
export function excludedIds(now: Date = new Date()): string[] {
  const state = loadState()
  const today = todayKey(now)
  const shownToday = Object.entries(state.shownOn)
    .filter(([, day]) => day === today)
    .map(([id]) => id)
  return Array.from(new Set([...state.dismissed, ...shownToday]))
}

export function markShown(id: string, now: Date = new Date()) {
  const state = loadState()
  state.shownOn[id] = todayKey(now)
  save(state)
}

/** 閉じられた = 興味なし。以後その端末では出さない。 */
export function markDismissed(id: string) {
  const state = loadState()
  if (!state.dismissed.includes(id)) state.dismissed.push(id)
  save(state)
}

/** デモ・検証用に状態を消す。 */
export function resetAnnouncementState() {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // 消せなくても実害はない
  }
}
